import { useMutation } from '@tanstack/react-query'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'

import type {
  SchemaCompletionUserProps,
  SchemaCompletionSystemProps,
  schemaCompletion,
  SchemaCompletionProps,
} from './server'

import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  parseTab,
  useTabsStore,
} from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import { invariant } from '@epic-web/invariant'
import { AsyncReturnType } from 'type-fest'
import { sleep } from '~/lib/sleep'

export const useSchemaAssistant = () => {
  const { data } = useSelectedTeamAndRepo()
  const { repo } = data || {}
  const activeTab = useTabsStore((s) => s.activeTab)
  const parsedActiveTab = activeTab ? parseTab(activeTab) : null
  const activeBlockId =
    parsedActiveTab?.type === 'block' ? parsedActiveTab.blockId : null

  const workingTreeRef = useWorkingTreeStore((s) => s.treeRef)

  const mutation = useMutation(async (props: SchemaCompletionUserProps) => {
    const minWaitTimePromise = sleep(250)

    try {
      if (!repo?.title || !workingTreeRef.current) {
        throw new Error('Not ready.')
      }

      const availableComponents: SchemaCompletionSystemProps['availableComponents'] =
        []

      for (const cId of (workingTreeRef.current.compoundIndexes.get(
        'components'
      ) ?? new Set()) as Set<string>) {
        const comp = workingTreeRef.current.getNode(cId, {
          required: true,
        }).value
        const { displayTitle, displayDescription } = getBlockDisplayData({
          block: comp,
          parentBlock: null,
        })
        if (displayTitle !== 'Untitled') {
          availableComponents.push({
            title: displayTitle,
            description: displayDescription,
            id: cId,
          })
        }
      }

      const activePath = activeBlockId
        ? workingTreeRef.current.getPath(activeBlockId, {
            required: true,
            ignoreRoot: true,
            format: 'array',
          })
        : null
      const ancestors = activePath
        ? activePath.map((id) => {
            const ancestor = workingTreeRef.current?.getNode(id, {
              required: true,
            })
            invariant(ancestor, 'ancestor is required')
            return { title: ancestor.value.title, type: ancestor.value.type }
          })
        : []

      const response = await fetch('/api/ai/schema-assistant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...props,
          availableComponents,
          ancestors,
          repositoryTitle: repo?.title,
        } satisfies SchemaCompletionProps),
      })

      if (response.status !== 200) {
        throw new Error("Couldn't fetch data.")
      }

      const data = (await response.json()) as AsyncReturnType<
        typeof schemaCompletion
      >

      return { output: data.output, availableComponents }
    } catch (error) {
      await minWaitTimePromise
      throw error
    }
  })

  return mutation
}

export type SchemaAssistantReturnType = ReturnType<typeof useSchemaAssistant>
